import axios from "axios";

const state = () => ({
  holes: [],
  boundsMapAndFlags: {},
  selectedCourse: null,
  selectedHole: null,
  enableSendToDevice: false,
  loading: false,
});

const mutations = {
  SET_HOLES(state, holes) {
    state.holes = holes;
  },
  SET_BOUNDS_MAP_AND_FLAGS(state, boundsMapAndFlags) {
    state.boundsMapAndFlags = boundsMapAndFlags;
  },
  SET_SELECTED_COURSE(state, course) {
    state.selectedCourse = course;
  },
  SET_ENABLE_SEND_TO_DEVICE(state, enable) {
    state.enableSendToDevice = enable;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_SELECTED_HOLE(state, hole) {
    state.selectedHole = hole;
  },
};

const getters = {
  getHoles: (state) => {
    return state.holes;
  },
  getBoundsMapAndFlags: (state) => {
    return state.boundsMapAndFlags;
  },
  getSelectedCourse: (state) => {
    return state.selectedCourse;
  },
  getEnableSendToDevice: (state) => {
    return state.enableSendToDevice;
  },
  getLoading: (state) => {
    return state.loading;
  },
  getSelectedHole: (state) => {
    return state.selectedHole;
  },
};

const actions = {
  async fetchHoles({ commit, dispatch }, id) {
    const url = process.env.VUE_APP_API_URL + `/utilities/holes/${id}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      commit("SET_HOLES", response.data.holes);
      if (response.data.holes.length === 0) {
        dispatch(
          "toastNotification/displayToast",
          {
            message: "No holes found for this course.",
            type: "warning",
          },
          { root: true }
        );
      }
      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `Failed to fetch course holes`,
          type: "error",
        },
        { root: true }
      );
      throw error.response?.data;
    }
  },

  async updateNodeCourseHoles({ dispatch, commit }, payload) {
    commit("SET_LOADING", true);
    const url =
      process.env.VUE_APP_API_URL + `/utilities/holes/${payload.courseId}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    for (let i = 0; i < payload.holes.length; i++) {
      if (payload.holes[i].displayHole === "") {
        payload.holes[i].displayHole = null;
      }
    }

    try {
      const response = await axios.patch(url, payload.holes, config);
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${response?.message || "Holes updated successfully."}`,
          type: "success",
        },
        { root: true }
      );
      commit("SET_ENABLE_SEND_TO_DEVICE", true);
      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            error?.response?.data?.message || "Failed to save holes"
          } `,
          type: "error",
        },
        { root: true }
      );
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchBoundsMapAndFlags({ commit, dispatch }, id) {
    const url =
      process.env.VUE_APP_API_URL +
      `/utilities/holes/getBoundsMapAndFlags/${id}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      commit("SET_BOUNDS_MAP_AND_FLAGS", response.data);
      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `Failed to fetch course maps and flags`,
          type: "error",
        },
        { root: true }
      );
      throw error.response?.data;
    }
  },

  async sendConfigToDevices({ dispatch, commit }, id) {
    commit("SET_LOADING", true);
    const url =
      process.env.VUE_APP_API_URL + `/utilities/holes/send-action/${id}`;

    let data = JSON.stringify({
      action: "get-config",
    });

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
        "Content-Type": "application/json",
      },
    };

    try {
      await axios.post(url, data, config);
      dispatch(
        "toastNotification/displayToast",
        {
          message: "Configuration sent to devices successfully.",
          type: "success",
        },
        { root: true }
      );
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `Failed to send configuration \n ${error?.message}`,
          type: "error",
        },
        { root: true }
      );
      throw error.response?.data;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async importHoles(_, payload) {
    const url =
      process.env.VUE_APP_API_URL + `/utilities/holes/import/${payload.id}`;

    const formData = new FormData();
    formData.append("uploaded_file", payload.file);

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
        "Content-Type": "multipart/form-data",
      },
    };

    try {
      const response = await axios.post(url, formData, config);
      return response.data;
    } catch (error) {
      if (error?.message) {
        throw error;
      } else {
        throw error?.response?.data;
      }
    }
  },

  async clearHoles({ dispatch, commit }, id) {
    commit("SET_LOADING", true);
    const url = process.env.VUE_APP_API_URL + `/utilities/holes/${id}/clear`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.delete(url, config);
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            response?.data?.message || "Holes cleared successfully."
          }`,
          type: "success",
        },
        { root: true }
      );
      commit("SET_HOLES", []);
      commit("SET_SELECTED_HOLE", null);
      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            error?.response?.data?.message || "Failed to clear holes"
          }`,
          type: "error",
        },
        { root: true }
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
